/**
 * EntityQuickSwitcher · R138bh · Feb 2026
 * ────────────────────────────────────────
 * Compact dropdown that lets the user jump between sibling entities
 * (projects, brands, clients …) without leaving the current sub-tab.
 *
 * Behaviour:
 *   • Renders a button showing the current entity name.
 *   • Clicking opens a searchable list of siblings (fetched once).
 *   • Selecting an item navigates to `buildPath(id)` and preserves the
 *     current URL search/hash so the user lands on the same sub-tab.
 *
 * Props
 *   currentId   — id of the entity currently open
 *   currentName — display name to render on the trigger button
 *   fetchItems  — async () => [{ id, name, code?, subtitle? }]
 *   buildPath   — (id) => string   (e.g. `/app/projects/${id}`)
 *   label       — trigger label prefix (e.g. "Project")
 *   testidBase  — data-testid prefix (e.g. "project-switcher")
 *
 * Keeps the search/hash so sub-tab state (e.g. `?tab=tasks`) survives
 * the switch.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronsUpDown, Check, Search as SearchIcon } from "lucide-react";

export default function EntityQuickSwitcher({
  currentId, currentName, fetchItems, buildPath, label = "Switch", testidBase = "entity-switcher",
  // Feb 2026 R293p · Caller-supplied trigger. When provided, the
  // consumer receives `{ open, toggle, label, currentName }` and
  // renders whatever element they like (e.g. the page title with a
  // small chevron). The popover still mounts anchored to the trigger.
  renderTrigger,
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [anchorRect, setAnchorRect] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchItems();
      setItems(Array.isArray(rows) ? rows : []);
    } finally { setLoading(false); }
  }, [fetchItems]);

  // Feb 2026 R293p · Portal-based popover so the switcher is never
  // clipped by an ancestor's `overflow:hidden` (e.g. the dense page
  // header's h1). Position recomputes on open + on resize/scroll.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && items.length === 0) load();
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open, items.length, load]);

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items.slice(0, 200);
    return items.filter((it) =>
      (it.name || "").toLowerCase().includes(needle) ||
      (it.code || "").toLowerCase().includes(needle) ||
      (it.subtitle || "").toLowerCase().includes(needle),
    ).slice(0, 200);
  }, [items, q]);

  const pick = (it) => {
    setOpen(false);
    setQ("");
    // Feb 2026 R293p · `buildPath` may already include the current
    // search string (callers used to do that defensively). To avoid
    // the doubled `?tab=` bug, only append loc.search when the path
    // doesn't carry one already.
    const path = buildPath(it.id) || "";
    const hasSearch = path.includes("?");
    nav(`${path}${hasSearch ? "" : (loc.search || "")}${loc.hash || ""}`);
  };

  const popover = open && anchorRect ? (
    <div
      ref={popRef}
      style={{
        position: "fixed",
        top: anchorRect.top + 6,
        left: Math.max(8, Math.min(anchorRect.left, (typeof window !== "undefined" ? window.innerWidth : 1024) - 360)),
        width: 340,
      }}
      className="bg-background border border-border shadow-xl z-[100] max-h-[420px] flex flex-col"
      data-testid={`${testidBase}-popover`}
    >
      <div className="relative border-b border-border">
        <SearchIcon className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}s…`}
          className="w-full h-9 pl-7 pr-2 text-xs bg-transparent focus:outline-none"
          data-testid={`${testidBase}-search`}
        />
      </div>
      <div className="overflow-y-auto flex-1 py-1">
        {loading ? (
          <div className="text-xs text-muted-foreground px-3 py-4">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-muted-foreground px-3 py-4">
            {q ? "No matches." : `No other ${label.toLowerCase()}s in this workspace.`}
          </div>
        ) : (
          filtered.map((it) => {
            const active = it.id === currentId;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => pick(it)}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-muted ${
                  active ? "bg-muted/70 font-semibold" : ""
                }`}
                data-testid={`${testidBase}-item-${it.id}`}
              >
                {active
                  ? <Check className="w-3 h-3 text-primary shrink-0" />
                  : <span className="w-3 h-3 shrink-0" />}
                <span className="truncate flex-1">{it.name || "(untitled)"}</span>
                {it.code && <span className="text-[10px] font-mono text-muted-foreground shrink-0">{it.code}</span>}
              </button>
            );
          })
        )}
      </div>
      {items.length > 0 && (
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-t border-border">
          {filtered.length} of {items.length}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="relative inline-flex">
      {renderTrigger ? (
        <span ref={btnRef} className="inline-flex max-w-full">
          {renderTrigger({
            open,
            toggle: () => setOpen((v) => !v),
            currentName,
            label,
          })}
        </span>
      ) : (
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 border border-border bg-background hover:bg-muted transition max-w-[200px] sm:max-w-[280px]"
          title={`${label}: ${currentName || "—"}. Click to switch.`}
          data-testid={`${testidBase}-trigger`}
        >
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{label}</span>
          <span className="truncate">{currentName || "—"}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-60 shrink-0" />
        </button>
      )}
      {popover && typeof document !== "undefined" ? createPortal(popover, document.body) : null}
    </div>
  );
}
